/**
 * Event Attribution service — Registration QR Phase 2.
 *
 * Wraps the bulk-reassign RPC + audit-log read. The RPC is
 * SECURITY DEFINER with role gate (manager/admin/superadmin) and
 * derives the actor from auth.uid().
 */

import { supabase } from "./supabase";
import type { UUID } from "../types/common.types";

export interface BulkReassignResultRow {
  attendee_id: UUID;
  old_effective_position_id: UUID | null;
  new_effective_position_id: UUID | null;
}

export interface AttributionOverrideRow {
  id: UUID;
  event_attendee_id: UUID;
  old_override_position_id: UUID | null;
  new_override_position_id: UUID | null;
  old_effective_position_id: UUID | null;
  new_effective_position_id: UUID | null;
  actor_user_id: UUID | null;
  reason: string | null;
  created_at: string;
}

/**
 * Reassign N attendees to a single target sales_position. Pass
 * `null` for target_position_id to clear the override / revert to
 * the original CTA-derived attribution.
 */
export async function bulkReassignAttendees(
  attendeeIds: UUID[],
  targetPositionId: UUID | null,
  reason: string | null,
): Promise<BulkReassignResultRow[]> {
  if (attendeeIds.length === 0) {
    return [];
  }
  const { data, error } = await supabase.rpc("bulk_reassign_event_attendees", {
    p_attendee_ids: attendeeIds,
    p_target_position_id: targetPositionId,
    p_reason: reason,
  });
  if (error) {
    throw error;
  }
  return (data ?? []) as BulkReassignResultRow[];
}

/**
 * Read the append-only audit log for a single attendee. Used by the
 * admin override section to show reassignment history.
 */
export async function listAttributionOverrides(
  attendeeId: UUID,
): Promise<AttributionOverrideRow[]> {
  const { data, error } = await supabase
    .from("event_attendee_attribution_overrides")
    .select("*")
    .eq("event_attendee_id", attendeeId)
    .order("created_at", { ascending: false });
  if (error) {
    throw error;
  }
  return (data ?? []) as AttributionOverrideRow[];
}

/**
 * Read all override rows for an entire event (for the admin section's
 * recent-activity feed).
 */
export async function listEventAttributionOverrides(
  eventId: UUID,
  limit = 50,
): Promise<AttributionOverrideRow[]> {
  const { data, error } = await supabase
    .from("event_attendee_attribution_overrides")
    .select("*, event_attendees!inner(event_id)")
    .eq("event_attendees.event_id", eventId)
    .order("created_at", { ascending: false })
    .limit(limit);
  if (error) {
    throw error;
  }
  return (data ?? []) as AttributionOverrideRow[];
}

// ────────────────────────────────────────────────────────────────────────────
// Member Verification (Event Member Attribution)
// Backend: get_event_signup_pool / verify_/unverify_event_member_signup /
// get_event_attribution_summary — SECURITY DEFINER, pool-scoped (anti-harvest),
// non-managers must be assigned to the event (event_sales_assignments).
// ────────────────────────────────────────────────────────────────────────────

export interface SignupPoolRow {
  contact_id: UUID;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  membership_id: string | null;
  phone_number: string | null;
  promo_code: string | null;
  signup_date: string | null;
  /** mig 1390: voucher = this event's QR code; promo = organization promo code; verified = verified earlier. */
  signal: "promo" | "voucher" | "verified";
  attributed_sales_position_id: UUID | null;
  already_attributed: boolean;
  /** mig 1390: exact sign-up time (voucher issued / Zuora subscription created / verified). */
  signup_at?: string | null;
  /** mig 1390, promo rows: the promo code that matched. */
  matched_code?: string | null;
  /** mig 1390, promo rows: only_event | voucher | checkin | verified | unassigned. */
  match_basis?: string | null;
  /** mig 1390, unassigned promo rows: the other overlapping shows of the same organization. */
  also_matched?:
    | { event_id: UUID; title: string | null; start_time: string | null }[]
    | null;
  /** mig 1390: the event's timezone, for showing signup_at in show time. */
  event_timezone?: string | null;
}

export interface AttributionByRep {
  sales_position_id: UUID | null;
  title: string | null;
  verified_count: number;
  auto: boolean;
}

export interface EventAttributionSummary {
  event_id: UUID;
  attendance_count: number | null;
  total_pool: number;
  total_verified: number;
  close_rate: number | null;
  single_rep_auto: boolean;
  by_rep: AttributionByRep[];
}

/** The candidate sign-up pool for an event, optionally filtered by a search term. */
export async function getEventSignupPool(
  eventId: UUID,
  search?: string | null,
): Promise<SignupPoolRow[]> {
  const { data, error } = await supabase.rpc("get_event_signup_pool", {
    p_event_id: eventId,
    p_search: search?.trim() ? search.trim() : null,
  });
  if (error) {
    throw error;
  }
  return (data ?? []) as SignupPoolRow[];
}

/** Verify a pooled sign-up as the caller's (or a manager-picked assigned rep's). */
export async function verifyEventMemberSignup(
  eventId: UUID,
  contactId: UUID,
  creditedSalesPositionId?: UUID | null,
): Promise<Record<string, unknown>> {
  const { data, error } = await supabase.rpc("verify_event_member_signup", {
    p_event_id: eventId,
    p_contact_id: contactId,
    p_credited_sales_position_id: creditedSalesPositionId ?? null,
  });
  if (error) {
    throw error;
  }
  return (data ?? {}) as Record<string, unknown>;
}

/** Undo a verification (verifier or leadership only). */
export async function unverifyEventMemberSignup(
  eventId: UUID,
  contactId: UUID,
): Promise<Record<string, unknown>> {
  const { data, error } = await supabase.rpc("unverify_event_member_signup", {
    p_event_id: eventId,
    p_contact_id: contactId,
  });
  if (error) {
    throw error;
  }
  return (data ?? {}) as Record<string, unknown>;
}

/** Per-rep verified counts + the event's close rate. */
export async function getEventAttributionSummary(
  eventId: UUID,
): Promise<EventAttributionSummary | null> {
  const { data, error } = await supabase.rpc("get_event_attribution_summary", {
    p_event_id: eventId,
  });
  if (error) {
    throw error;
  }
  return (data ?? null) as EventAttributionSummary | null;
}
